import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];
const px = (id) => `/images/pexels-${id}.jpeg`;

const INGREDIENTS = [
  { id: "cashews", name: "Cashews", img: px(9811624), note: "The buttery base. Creamy texture and heart-healthy fats in every bite." },
  { id: "almonds", name: "Almonds", img: px(4033325), note: "Roasted for depth. Vitamin E, crunch, and slow-release energy." },
  { id: "dates", name: "Dates", img: px(15913423), note: "Nature's caramel. Binds and sweetens the bar honestly — no refined sugar, ever." },
  { id: "cocoa", name: "Cocoa Powder", img: px(1212845), note: "Raw and antioxidant-dense. Where the chocolate honesty comes from." },
  { id: "cocoa-butter", name: "Cocoa Butter", img: px(14132827), note: "Silky melt and richness. The clean fat that gives every bite its truffle-like finish." },
  { id: "whey", name: "Whey Protein", img: px(15120889), note: "Fast-absorbing and complete. The gold-standard amino profile for repair." },
  { id: "pea", name: "Pea Protein", img: px(768093), note: "Whey's gentle partner. Plant-based balance that keeps the blend bloat-free." },
  { id: "collagen", name: "Collagen", img: px(14832252), note: "The structural protein of skin, hair and joints. Radiance, fed from within." },
  { id: "creatine", name: "Creatine", img: px(12625114), note: "The most researched performance molecule on earth. Strength, power, output." },
  { id: "glutamine", name: "L-Glutamine", img: px(13779116), note: "The recovery amino. Repairs muscle and gut lining while you rest." },
  { id: "ashwagandha", name: "Ashwagandha", img: px(7615608), note: "Ayurveda's adaptogen, backed by modern trials. Calm inside the chaos." },
];

const Formula = () => (
  <section id="formula" className="py-32 md:py-48 border-b border-[#EBE8E3]" data-testid="formula-section">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, ease: EASE }}
        className="grid md:grid-cols-12 gap-8 mb-16 md:mb-20"
      >
        <p className="md:col-span-3 text-sm md:text-base tracking-[0.3em] uppercase text-[#C07C5F]">
          The Formula
        </p>
        <div className="md:col-span-9">
          <h2 className="font-serif-lux font-medium text-5xl md:text-7xl tracking-tighter leading-[0.95]">
            Nothing to hide,
            <span className="italic font-light text-[#5C5C5C]"> everything to show.</span>
          </h2>
          <p className="mt-8 max-w-xl text-sm md:text-base font-light text-[#5C5C5C] leading-relaxed" data-testid="formula-intro">
            Eleven ingredients. Each one natural, science-backed, and chosen to make
            a complete nutritional bar — not a candy bar in disguise. This is
            exactly what you're putting inside your body.
          </p>
        </div>
      </motion.div>

      <div className="grid grid-cols-2 md:grid-cols-5 border-t border-l border-[#EBE8E3]">
        {INGREDIENTS.map((ing, i) => (
          <motion.div
            key={ing.id}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: (i % 5) * 0.08, ease: EASE }}
            data-testid={`ingredient-card-${ing.id}`}
            className="group border-r border-b border-[#EBE8E3] bg-transparent hover:bg-white transition-colors duration-700"
          >
            <div className="overflow-hidden aspect-square">
              <img
                src={ing.img}
                alt={ing.name}
                loading="lazy"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-1000"
              />
            </div>
            <div className="p-6 md:p-7">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C07C5F] mb-2">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="font-serif-lux text-2xl md:text-[1.7rem] font-medium tracking-tight leading-none mb-3">
                {ing.name}
              </h3>
              <p className="text-xs font-light text-[#5C5C5C] leading-relaxed">
                {ing.note}
              </p>
            </div>
          </motion.div>
        ))}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.08, ease: EASE }}
          data-testid="ingredient-card-nothing-else"
          className="border-r border-b border-[#EBE8E3] bg-[#111111] text-[#F9F8F6] p-6 md:p-7 flex flex-col justify-end"
        >
          <h3 className="font-serif-lux italic font-light text-3xl md:text-4xl tracking-tight leading-none mb-4">
            Nothing else.
          </h3>
          <p className="text-xs font-light text-white/60 leading-relaxed">
            No artificial glazes. No cheap fillers. No refined sugar. No bloat.
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: EASE }}
        className="border border-t-0 border-[#EBE8E3] bg-[#111111] text-[#F9F8F6] px-8 md:px-14 py-12 md:py-16 grid md:grid-cols-12 gap-8 items-center"
        data-testid="formula-blend-banner"
      >
        <p className="md:col-span-4 text-sm md:text-base tracking-[0.3em] uppercase text-[#C07C5F]">
          The Bloat-Free Blend
        </p>
        <p className="md:col-span-8 font-serif-lux font-light text-2xl md:text-4xl leading-snug tracking-tight">
          Most bars lean on whey alone — and you feel it. We blend{" "}
          <span className="italic text-[#C07C5F]">whey with pea protein</span>{" "}
          for a complete amino profile, without the bloat other brands leave behind.
        </p>
      </motion.div>
    </div>
  </section>
);

export default Formula;
